// Parental Lock Service - PIN-based access control

// Simple hash function for PIN encoding (not cryptographically secure, but sufficient for local storage)
const hashPIN = (pin) => {
  let hash = 0;
  for (let i = 0; i < pin?.length; i++) {
    const char = pin?.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  return hash?.toString(36);
};

// Check if parental lock is enabled
export const isLockEnabled = () => {
  const lockSettings = localStorage?.getItem('parentalLockSettings');
  if (!lockSettings) return false;
  
  try {
    const settings = JSON?.parse(lockSettings);
    return settings?.enabled === true && settings?.hashedPIN;
  } catch {
    return false;
  }
};

// Setup parental lock with PIN
export const setupLock = (pin) => {
  if (!pin || pin?.length < 4) {
    throw new Error('PIN must be at least 4 digits');
  }
  
  const lockSettings = {
    enabled: true,
    hashedPIN: hashPIN(pin),
    createdAt: new Date()?.toISOString(),
    autoLockTimeout: 15 // minutes
  };
  
  localStorage?.setItem('parentalLockSettings', JSON?.stringify(lockSettings));
  return true;
};

// Validate PIN
export const validatePIN = (pin) => {
  const lockSettings = localStorage?.getItem('parentalLockSettings');
  if (!lockSettings) return false;
  
  try {
    const settings = JSON?.parse(lockSettings);
    return hashPIN(pin) === settings?.hashedPIN;
  } catch {
    return false;
  }
};

// Unlock session (temporary unlock until browser close or timeout)
export const unlockSession = () => {
  const unlockData = {
    unlockedAt: Date?.now(),
    expiresAt: Date?.now() + (15 * 60 * 1000) // 15 minutes
  };
  sessionStorage?.setItem('parentalLockSession', JSON?.stringify(unlockData));
};

// Check if session is currently unlocked
export const isSessionUnlocked = () => {
  if (!isLockEnabled()) return true; // No lock enabled, always unlocked
  
  const sessionData = sessionStorage?.getItem('parentalLockSession');
  if (!sessionData) return false;
  
  try {
    const data = JSON?.parse(sessionData);
    const now = Date?.now();
    
    // Check if session expired
    if (now > data?.expiresAt) {
      sessionStorage?.removeItem('parentalLockSession');
      return false;
    }
    
    return true;
  } catch {
    return false;
  }
};

// Lock session (clear unlock state)
export const lockSession = () => {
  sessionStorage?.removeItem('parentalLockSession');
};

// Disable parental lock
export const disableLock = () => {
  localStorage?.removeItem('parentalLockSettings');
  sessionStorage?.removeItem('parentalLockSession');
};

// Change PIN
export const changePIN = (oldPIN, newPIN) => {
  if (!validatePIN(oldPIN)) {
    throw new Error('Current PIN is incorrect');
  }
  
  if (!newPIN || newPIN?.length < 4) {
    throw new Error('New PIN must be at least 4 digits');
  }
  
  const lockSettings = JSON?.parse(localStorage?.getItem('parentalLockSettings'));
  lockSettings.hashedPIN = hashPIN(newPIN);
  lockSettings.updatedAt = new Date()?.toISOString();
  
  localStorage?.setItem('parentalLockSettings', JSON?.stringify(lockSettings));
  return true;
};

// Get lock settings (without sensitive data)
export const getLockSettings = () => {
  const lockSettings = localStorage?.getItem('parentalLockSettings');
  if (!lockSettings) return null;
  
  try {
    const settings = JSON?.parse(lockSettings);
    return {
      enabled: settings?.enabled,
      createdAt: settings?.createdAt,
      updatedAt: settings?.updatedAt,
      autoLockTimeout: settings?.autoLockTimeout
    };
  } catch {
    return null;
  }
};

// Extend session timeout (reset timer)
export const extendSession = () => {
  if (isSessionUnlocked()) {
    unlockSession(); // Reset the timer
  }
};
